// Phase COTTON-RM-002 Part B — Cotton Movement Log.
// Reuses the existing StockLedger backbone via GET /stock-ledger/flat.
// New transaction types (PROVISIONAL_IN, PROVISIONAL_RELEASE,
// LOCATION_CHANGE, QR_SCAN) added in Part A appear automatically here.

import { useMemo, useState } from 'react'
import {
  Button, Card, DatePicker, Empty, Input, Select, Skeleton, Space, Table,
  Tag, Typography,
} from 'antd'
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'

import apiClient from '@/shared/api/client'

const { Text, Title } = Typography

interface Plant { id: string; plant_name: string; plant_code?: string }

interface LedgerRow {
  id: string
  plant_id: string
  transaction_date: string
  transaction_type: string
  reference_doc_no: string | null
  item_id: string | null
  item_name?: string | null
  lot_no: string | null
  bale_id?: string | null
  bale_no?: string | null
  godown_from?: string | null
  godown_to?: string | null
  quantity_kg: number | null
  user_id?: string | null
  remarks: string | null
}

const TX_TYPES = [
  'GRN', 'IDT_OUT', 'IDT_IN', 'MIXING_ISSUE', 'WASTE', 'REJECTION',
  'ADJUSTMENT', 'RETURN', 'QC_HOLD', 'QC_RELEASE',
  // Phase COTTON-RM-002 Part A additions
  'PROVISIONAL_IN', 'PROVISIONAL_RELEASE', 'LOCATION_CHANGE', 'QR_SCAN',
]

function txColor(t: string) {
  if (t === 'PROVISIONAL_IN' || t === 'GRN' || t === 'IDT_IN') return 'green'
  if (t === 'PROVISIONAL_RELEASE' || t === 'MIXING_ISSUE'
      || t === 'IDT_OUT' || t === 'WASTE' || t === 'REJECTION'
      || t === 'RETURN') return 'orange'
  if (t === 'QC_HOLD') return 'warning'
  if (t === 'QC_RELEASE') return 'success'
  if (t === 'QR_SCAN') return 'blue'
  if (t === 'LOCATION_CHANGE') return 'cyan'
  return 'default'
}

function exportCsv(rows: LedgerRow[]) {
  const cols = ['transaction_date', 'transaction_type', 'reference_doc_no',
                'lot_no', 'bale_no', 'godown_from', 'godown_to', 'quantity_kg',
                'remarks']
  const header = cols.join(',')
  const lines = rows.map(r => cols.map(c => {
    const v = (r as unknown as Record<string, unknown>)[c]
    if (v === null || v === undefined) return ''
    const s = String(v)
    return s.includes(',') ? `"${s.replace(/"/g, '""')}"` : s
  }).join(','))
  const blob = new Blob([[header, ...lines].join('\n')], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `cotton-movements-${new Date().toISOString().slice(0, 10)}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

export default function CottonMovementPage() {
  const [plantId, setPlantId] = useState<string | undefined>()
  const [txType, setTxType] = useState<string | undefined>()
  const [lotNo, setLotNo] = useState<string>('')
  const [limit, setLimit] = useState<number>(500)

  const { data: plants = [] } = useQuery({
    queryKey: ['plants-for-cotton-movement'],
    queryFn: async () => (await apiClient.get<Plant[]>('/plants')).data,
  })

  const { data: rows = [], isLoading, refetch, isFetching } = useQuery({
    queryKey: ['cotton-movements', plantId, txType, lotNo, limit],
    queryFn: () => apiClient.get<LedgerRow[]>('/stock-ledger/flat', {
      params: {
        plant_id: plantId,
        transaction_type: txType,
        lot_no: lotNo || undefined,
        limit,
      },
    }).then(r => r.data),
    enabled: !!plantId,
  })

  const columns = [
    { title: 'Date / Time', dataIndex: 'transaction_date', width: 160,
      render: (s: string) => new Date(s).toLocaleString() },
    { title: 'Type', dataIndex: 'transaction_type', width: 160,
      render: (t: string) => <Tag color={txColor(t)}>{t}</Tag> },
    { title: 'Ref Doc', dataIndex: 'reference_doc_no', width: 140,
      render: (v: string | null) => v ?? '—' },
    { title: 'Lot', dataIndex: 'lot_no', width: 120,
      render: (v: string | null) => v ?? '—' },
    { title: 'Bale', dataIndex: 'bale_no', width: 140,
      render: (v: string | null) => v ? <code>{v}</code> : '—' },
    { title: 'From', dataIndex: 'godown_from', width: 130,
      render: (v: string | null) => v ?? '—' },
    { title: 'To', dataIndex: 'godown_to', width: 130,
      render: (v: string | null) => v ?? '—' },
    { title: 'Qty kg', dataIndex: 'quantity_kg', width: 120,
      render: (n: number | null) => n ? n.toLocaleString() : '—' },
    { title: 'Remarks', dataIndex: 'remarks', ellipsis: true,
      render: (v: string | null) => v ?? '' },
  ]

  return (
    <div style={{ padding: 16, maxWidth: 1600, margin: '0 auto' }}>
      <Title level={2} style={{ marginTop: 0 }}>Cotton Movements</Title>
      <Text type="secondary">
        Stock movement log driven by the existing <code>StockLedger</code> table.
        Provisional/QR/location events introduced in Part A appear here
        automatically — no parallel movement system.
      </Text>

      <Card style={{ marginTop: 12 }}
            extra={<Space>
              <Button icon={<ReloadOutlined />} onClick={() => refetch()}
                       loading={isFetching} disabled={!plantId}>
                Refresh</Button>
              <Button icon={<DownloadOutlined />}
                       disabled={!rows.length}
                       onClick={() => exportCsv(rows)}>Export CSV</Button>
            </Space>}>
        <Space wrap style={{ marginBottom: 12 }}>
          <Select placeholder="Plant" style={{ width: 220 }}
                  value={plantId} onChange={setPlantId}
                  options={plants.map(p => ({ value: p.id, label: p.plant_name }))} />
          <Select placeholder="Transaction type" value={txType}
                  onChange={setTxType} allowClear style={{ width: 220 }}
                  options={TX_TYPES.map(t => ({ value: t, label: t }))} />
          <Input placeholder="Lot no" value={lotNo}
                  onChange={e => setLotNo(e.target.value)}
                  style={{ width: 160 }} allowClear />
          <Select placeholder="Limit" value={limit} onChange={setLimit}
                  options={[100, 250, 500, 1000, 2500].map(n => ({ value: n, label: `${n} rows` }))}
                  style={{ width: 120 }} />
        </Space>

        {!plantId ? (
          <Empty description="Pick a plant to load movements." />
        ) : isLoading ? <Skeleton active /> : (
          rows.length === 0
            ? <Empty description="No movements for these filters." />
            : (
              <Table
                rowKey="id"
                columns={columns as never}
                dataSource={rows}
                size="small"
                pagination={{ pageSize: 50 }}
                scroll={{ x: 1300 }}
              />
            )
        )}
      </Card>
    </div>
  )
}
